// Shared "deal block" — the single source of truth for rendering a Deal
// as a card (dashboard grid) or a row (deal lists + scenario lists).
//
// Deal-forward PR B consolidates four previously-bespoke renders behind
// this module:
//   1. Dashboard "Recent deals" shelf cards         (card variant)
//   2. `/deals/` + `/<project>/deals/` list rows     (row variant + compare)
//   3. `/parcels/<uid>/` scenario rows               (row variant)
//   4. Project Detail pin-card scenario rows         (row variant + compare)
//
// Each surface keeps its own surrounding layout (shelf board, list
// `<ul>`, group headings, pin-card body) and per-container framing in
// `_dealblock.scss`; only the per-deal unit lives here. The pure helpers
// (`dealBlockTitle`, `dealBlockModeLabel`, `dealBlockHref`,
// `dealBlockRelativeTime`) are exported so non-rendering callers stay
// consistent with the component.

import {IDeal} from '../views/dealcalc/storage';

// Normalized deal-summary shape — a superset of every source. The rich
// surfaces (dashboard shelf, deals list) fill all fields; the minimal
// scenario refs (`IParcelAttachedDealRef`, `IAttachedDealRef`) carry only
// `slug` / `title` / `projectSlug`. Everything except `slug` is optional
// so callers map in whatever data they actually have, and the renderer
// shows only what's present.
export interface IDealBlockData {
	slug: string;
	title?: string | null;
	// Slug of the project the deal is attached to, or null/absent for a
	// standalone deal. Drives the default href + the scope chip.
	projectSlug?: string | null;
	// Property address from `state_json.property.address`; the dominant
	// display title when present.
	address?: string | null;
	// `state_json.mode` ('reno' | 'build'); null/absent → no mode shown.
	mode?: string | null;
	lastModified?: string | null;
	// Optional pre-computed grade badge. PR B intentionally does NOT
	// compute this client-side — importing `gradeDeal` / `computeDealMetrics`
	// would pull the deal-calc engine into the dashboard + projectdetail
	// bundles (plan §6 Q1). A later PR can populate it server-side on
	// `DealSerializer` and it renders here automatically.
	grade?: {label: string; color?: string} | null;
}

// Compare-selection checkbox wiring (row variant only). `onChange`
// receives the checkbox's new checked state; the caller owns the
// selection set + any re-render.
export interface IDealBlockCompare {
	checked: boolean;
	disabled?: boolean;
	onChange: (checked: boolean) => void;
}

export interface IDealBlockOptions {
	variant: 'card' | 'row';
	// href override. Default: `dealBlockHref(data)` (the deal's own
	// project-or-flat scope). Surfaces whose link target differs from the
	// deal's home scope — a project-scoped list that links into its own
	// editor URL, a parcel rollup group — pass this explicitly.
	href?: string;
	// Show the project / standalone scope (chip on card, meta token on
	// row). Default: card → true, row → false (row lists are usually
	// already scope-filtered).
	showProject?: boolean;
	// Show the deal mode (chip on card, leading meta token on row).
	// Default true, but only renders when `mode` resolves to a known
	// label.
	showMode?: boolean;
	// Show the relative "last modified" stamp (card only). Default true.
	showTime?: boolean;
	// Show the slug (card footer / row meta tail). Default true.
	showSlug?: boolean;
	// Compare-selection checkbox (row only). Omit for no checkbox.
	compare?: IDealBlockCompare | null;
	// Accessible label override for the card link.
	ariaLabel?: string;
}

const UNTITLED = 'Untitled scenario';

// Display-title priority, unified across all four surfaces: property
// address first (the user's strongest identifier), then a meaningful
// model title, finally the slug. The `Untitled scenario` placeholder is
// treated as "no title" so it never wins over the slug.
export function dealBlockTitle(data: IDealBlockData): string {
	const address = (data.address || '').trim();
	if (address) {
		return address;
	}
	const title = (data.title || '').trim();
	if (title && title !== UNTITLED) {
		return title;
	}
	return data.slug;
}

// Mode → {key, label}. Returns null for unknown / absent mode so minimal
// scenario refs (which carry no `state_json`) render no mode token.
export function dealBlockModeLabel(data: IDealBlockData): {key: string; label: string} | null {
	if (data.mode === 'build') {
		return {key: 'build', label: 'New build'};
	}
	if (data.mode === 'reno') {
		return {key: 'reno', label: 'Renovation'};
	}
	return null;
}

// Default link target: project-scoped editor URL for attached deals, the
// flat editor URL for standalone deals. Mirrors the partition rule used
// everywhere else (PR #66+).
export function dealBlockHref(data: IDealBlockData): string {
	if (data.projectSlug) {
		return `/${encodeURIComponent(data.projectSlug)}/deals/${encodeURIComponent(data.slug)}/`;
	}
	return `/deals/${encodeURIComponent(data.slug)}/`;
}

// Compact relative-time label ("3d", "2h", "just now") from an ISO
// timestamp. Returns '' for missing / unparseable input so callers can
// skip rendering. `Date.parse` / `Date.now` are fine in the browser
// bundle — the no-arg `new Date()` workflow-runtime restriction doesn't
// apply here.
export function dealBlockRelativeTime(iso: string | null | undefined): string {
	if (!iso) {
		return '';
	}
	const then = Date.parse(iso);
	if (Number.isNaN(then)) {
		return '';
	}
	const diffMs = Date.now() - then;
	if (diffMs < 0) {
		return 'just now';
	}
	const mins = Math.floor(diffMs / 60000);
	if (mins < 1) {
		return 'just now';
	}
	if (mins < 60) {
		return `${mins}m`;
	}
	const hours = Math.floor(mins / 60);
	if (hours < 24) {
		return `${hours}h`;
	}
	const days = Math.floor(hours / 24);
	if (days < 30) {
		return `${days}d`;
	}
	const months = Math.floor(days / 30);
	if (months < 12) {
		return `${months}mo`;
	}
	return `${Math.floor(months / 12)}y`;
}

// A scenario name is the model title when it's a real differentiator —
// i.e. distinct from the address (which is already the display title) and
// not the placeholder. Used as the middle meta token on rich rows.
function scenarioName(data: IDealBlockData): string {
	const title = (data.title || '').trim();
	const address = (data.address || '').trim();
	if (title && title !== address && title !== UNTITLED) {
		return title;
	}
	return '';
}

// ─────────── Normalizers ───────────

// `IDealSummary` (ambient global) → block data. Backs the dashboard
// "Recent deals" shelf.
export function dealBlockFromSummary(deal: IDealSummary): IDealBlockData {
	const state = deal.stateJson || {};
	const property = state.property || {};
	return {
		slug: deal.slug,
		title: deal.title,
		projectSlug: deal.projectSlug,
		address: property.address || null,
		mode: state.mode || null,
		lastModified: deal.lastModified,
	};
}

// `IDeal` (dealcalc editor/storage shape) → block data. Backs the
// `/deals/` + `/<project>/deals/` list rows.
export function dealBlockFromEditorDeal(deal: IDeal): IDealBlockData {
	const state = deal.stateJson || ({} as IDeal['stateJson']);
	const property = (state && state.property) || ({} as {address?: string});
	return {
		slug: deal.slug,
		title: deal.title,
		projectSlug: deal.projectSlug,
		address: property.address || null,
		mode: state ? state.mode : null,
		lastModified: deal.lastModified,
	};
}

// ─────────── Rendering ───────────

function el<K extends keyof HTMLElementTagNameMap>(
	tagName: K,
	className?: string,
	text?: string,
): HTMLElementTagNameMap[K] {
	const node = document.createElement(tagName);
	if (className) {
		node.className = className;
	}
	if (text !== undefined) {
		node.textContent = text;
	}
	return node;
}

export function renderDealBlock(data: IDealBlockData, opts: IDealBlockOptions): HTMLElement {
	return opts.variant === 'card'
		? renderCard(data, opts)
		: renderRow(data, opts);
}

function renderCard(data: IDealBlockData, opts: IDealBlockOptions): HTMLElement {
	const showProject = opts.showProject !== false;
	const showMode = opts.showMode !== false;
	const showTime = opts.showTime !== false;
	const showSlug = opts.showSlug !== false;
	const title = dealBlockTitle(data);

	const card = el('a', 'lb-deal-block lb-deal-block--card');
	card.href = opts.href || dealBlockHref(data);
	card.setAttribute('aria-label', opts.ariaLabel || `Open deal ${title}`);

	const top = el('div', 'lb-deal-block__top');
	const mode = dealBlockModeLabel(data);
	if (showMode && mode) {
		top.appendChild(el('span', `lb-deal-block__mode lb-deal-block__mode--${mode.key}`, mode.label));
	}
	if (data.grade) {
		const grade = el('span', 'lb-deal-block__grade', data.grade.label);
		if (data.grade.color) {
			grade.style.color = data.grade.color;
		}
		top.appendChild(grade);
	}
	if (showTime) {
		const rel = dealBlockRelativeTime(data.lastModified);
		if (rel) {
			top.appendChild(el('span', 'lb-deal-block__time', rel));
		}
	}
	if (top.childNodes.length > 0) {
		card.appendChild(top);
	}

	card.appendChild(el('h3', 'lb-deal-block__title', title));

	const footer = el('div', 'lb-deal-block__footer');
	if (showProject) {
		const standalone = !data.projectSlug;
		const scope = el('span', standalone
			? 'lb-deal-block__scope lb-deal-block__scope--standalone'
			: 'lb-deal-block__scope');
		scope.appendChild(el('span', 'material-icons', standalone ? 'bolt' : 'folder'));
		scope.appendChild(el('span', '', standalone ? 'Standalone' : (data.projectSlug as string)));
		footer.appendChild(scope);
	}
	if (showSlug) {
		footer.appendChild(el('span', 'lb-deal-block__slug', data.slug));
	}
	if (footer.childNodes.length > 0) {
		card.appendChild(footer);
	}
	return card;
}

function renderRow(data: IDealBlockData, opts: IDealBlockOptions): HTMLElement {
	// Row defaults differ from the card: scope is off (lists are usually
	// already scope-filtered), mode + slug on.
	const showProject = opts.showProject === true;
	const showMode = opts.showMode !== false;
	const showSlug = opts.showSlug !== false;
	const title = dealBlockTitle(data);

	const li = el('li', 'lb-deal-block lb-deal-block--row');

	if (opts.compare) {
		const compare = opts.compare;
		const label = el('label', 'lb-deal-block__select');
		const cb = document.createElement('input');
		cb.type = 'checkbox';
		cb.className = 'lb-deal-block__select-input';
		cb.checked = compare.checked;
		if (compare.disabled) {
			cb.disabled = true;
		}
		// Clicking the checkbox must not bubble to any row/link handler.
		cb.addEventListener('click', evt => evt.stopPropagation());
		cb.addEventListener('change', () => compare.onChange(cb.checked));
		label.appendChild(cb);
		li.appendChild(label);
	}

	const link = el('a', 'lb-deal-block__link');
	link.href = opts.href || dealBlockHref(data);
	link.appendChild(el('span', 'lb-deal-block__title', title));

	const metaParts: Array<string> = [];
	const mode = dealBlockModeLabel(data);
	if (showMode && mode) {
		metaParts.push(mode.label);
		const sname = scenarioName(data);
		if (sname) {
			metaParts.push(sname);
		}
	}
	if (showProject) {
		metaParts.push(data.projectSlug ? data.projectSlug : 'Standalone');
	}
	if (showSlug) {
		metaParts.push(data.slug);
	}
	if (metaParts.length > 0) {
		link.appendChild(el('span', 'lb-deal-block__meta', metaParts.join(' · ')));
	}
	li.appendChild(link);
	return li;
}
